import React, { useMemo } from 'react';
import { clsx } from 'clsx';
import { useStore } from '@tanstack/react-form';
import type { TradeForm, SearchInventoryItem, CalculatedValues } from './types';
import { inputClassName, labelClassName, readOnlyInputClassName, isAmendLocked } from './types';
import type { HolidayCheckResponse, TradeType } from '@/types';
import { showsClosingCash } from '@/utils/trades/closing-cash';
import { getWeekendDayName } from '@/services/trades/trade-validation';

// UTC, like the trade date itself and the backend's "has the trade date come" check
// (LR-809): a local "today" flags a pristine form as backdated after local midnight.
const getTodayIsoDate = (): string => new Date().toISOString().split('T')[0];

// When trade_date moves past settlement_date, return a new settlement date
// (trade_date + 1 day). Returns undefined if no shift is needed.
// eslint-disable-next-line react-refresh/only-export-components -- date helper paired with the section component; splitting needs cross-section import updates
export const computeSettlementShiftOnTradeDateChange = (
  newTradeDate: string | undefined,
  currentSettlement: string | undefined,
): string | undefined => {
  if (!newTradeDate || !currentSettlement) return undefined;
  if (currentSettlement >= newTradeDate) return undefined;
  const t = new Date(newTradeDate);
  t.setDate(t.getDate() + 1);
  return t.toISOString().split('T')[0];
};

interface PositionDatesSectionProps {
  form: TradeForm;
  isSubmitted: boolean;
  isFormDisabled: boolean;
  tradeDuration: string | undefined;
  settlementDate: string | undefined;
  selectedInventoryItem: SearchInventoryItem | null;
  /** Security currency for the read-only return/closing cells. Tracks the picked
   *  item in create, the booked position currency in amend (LR-495). */
  securityCurrency: string;
  calculatedValues: CalculatedValues;
  tradeDateHoliday?: HolidayCheckResponse;
  settlementDateHoliday?: HolidayCheckResponse;
  amendMode?: boolean;
}

export const PositionDatesSection: React.FC<PositionDatesSectionProps> = ({
  form,
  isSubmitted,
  isFormDisabled,
  tradeDuration,
  settlementDate,
  selectedInventoryItem,
  securityCurrency,
  calculatedValues,
  tradeDateHoliday,
  settlementDateHoliday,
  amendMode,
}) => {
  // LR-495: booked currency in amend, picked-item currency in create.
  const displayCurrency = amendMode ? securityCurrency : selectedInventoryItem?.currency;
  const todayIsoDate = useMemo(getTodayIsoDate, []);
  // LR-367: GMRA cash-flow interest is intentionally NOT the same as
  // `totalReturn` (signed PnL × days). For repo with positive rate the closing
  // cash grows (we owe more back) while our PnL is negative — `totalReturn`
  // carries pnl_direction, the closing cash leg does not. Reconstruct the raw
  // interest leg here as `closingSettlement − netSettlement` so the tooltip
  // arithmetic actually matches the displayed value.
  //
  // Subscribed via useStore, not read off form.state: the trade type gates
  // whether Closing Settlement RENDERS, so switching it must re-render here.
  const tradeType = useStore(form.store, (state) => state.values.trade_type) as
    | TradeType
    | undefined;
  const showClosingSettlement = showsClosingCash({ trade_type: tradeType });
  const gmraCashFlowInterest =
    showClosingSettlement &&
    calculatedValues.closingSettlement !== undefined &&
    calculatedValues.netSettlementSecurity !== undefined
      ? calculatedValues.closingSettlement - calculatedValues.netSettlementSecurity
      : undefined;
  return (
    <div className="p-6 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <form.Field name="trade_duration">
            {(field) => (
              <>
                <label className={labelClassName}>
                  Trade Duration
                </label>
                <select
                  value={field.state.value || 'OPEN'}
                  onChange={(e) => {
                    const value = e.target.value as 'OPEN' | 'EVERGREEN' | 'TERM';
                    field.handleChange(value);

                    // Update term_type based on trade_duration
                    form.setFieldValue('term_type', value === 'TERM' ? 'term' : value === 'OPEN' ? 'open' : 'evergreen');

                    if (value === 'OPEN') {
                      form.setFieldValue('maturity_date', '');
                      form.setFieldValue('notice_period_days', undefined);
                    } else if (value === 'TERM') {
                      form.setFieldValue('notice_period_days', undefined);
                    } else if (value === 'EVERGREEN') {
                      form.setFieldValue('notice_period_days', 1);
                      // LR-684: Evergreen rolls via an empty maturity_date until
                      // Give Notice sets it - a pre-set date here would mature the
                      // position with notice never served (backend rejects it now).
                      form.setFieldValue('maturity_date', '');
                    }
                  }}
                  disabled={isFormDisabled || isAmendLocked(amendMode, 'trade_duration')}
                  className={clsx(inputClassName, isFormDisabled && 'disabled:bg-gray-100 disabled:cursor-not-allowed')}
                >
                  <option value="OPEN">OPEN</option>
                  <option value="EVERGREEN">EVERGREEN</option>
                  <option value="TERM">TERM</option>
                </select>
              </>
            )}
          </form.Field>
        </div>
        <div>
          <form.Field
            name="trade_date"
            validators={{
              onChange: ({ value }) => {
                if (!value) return 'Trade date is required';
                if (form.state.values.settlement_date && value > form.state.values.settlement_date) {
                  return 'Settlement date cannot be before trade date';
                }
                if (form.state.values.trade_duration === 'TERM' && form.state.values.maturity_date && value >= form.state.values.maturity_date) {
                  return 'Maturity date must be after trade date';
                }
                return undefined;
              },
            }}
          >
            {(field) => {
              const hasDateWarning = !!field.state.value && field.state.value !== todayIsoDate;
              const isBackdated = !!field.state.value && field.state.value < todayIsoDate;
              const tradeDateWeekend = getWeekendDayName(field.state.value);
              return (
                <>
                  <label className={labelClassName}>
                    Trade Date <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-1">
                    <input
                      type="date"
                      value={field.state.value}
                      onChange={(e) => {
                        const newTradeDate = e.target.value;
                        // Keep settlement_date on or after trade_date BEFORE running
                        // the trade_date validator — otherwise it reads the stale
                        // settlement value, fires the "Settlement before trade" error,
                        // and suppresses the yellow in-the-future warning.
                        const shifted = computeSettlementShiftOnTradeDateChange(
                          newTradeDate,
                          form.state.values.settlement_date,
                        );
                        if (shifted) form.setFieldValue('settlement_date', shifted);
                        field.handleChange(newTradeDate);
                      }}
                      disabled={isFormDisabled || isAmendLocked(amendMode, 'trade_date')}
                      className={clsx(
                        inputClassName,
                        'flex-1',
                        isSubmitted && field.state.meta.errors.length > 0 && 'border-red-500',
                        hasDateWarning && field.state.meta.errors.length === 0 && 'border-yellow-500',
                        isFormDisabled && 'disabled:bg-gray-100 disabled:cursor-not-allowed'
                      )}
                    />
                    {/* trade_time gets its own Field so the controlled <input>
                        actually re-renders on change. Reading
                        form.state.values.trade_time from inside the trade_date
                        Field (which only subscribes to trade_date) left the
                        input unsubscribed, so React restored the stale value and
                        the picked time appeared to "do nothing" (feedback p.5). */}
                    <form.Field name="trade_time">
                      {(timeField) => (
                        <input
                          type="time"
                          step="1"
                          value={timeField.state.value || ''}
                          onChange={(e) => timeField.handleChange(e.target.value)}
                          disabled={isFormDisabled || isAmendLocked(amendMode, 'trade_time')}
                          title="Trade time, UTC (hh:mm:ss)"
                          className={clsx(
                            inputClassName,
                            'w-[120px]',
                            isFormDisabled && 'disabled:bg-gray-100 disabled:cursor-not-allowed'
                          )}
                        />
                      )}
                    </form.Field>
                    <span className="self-center text-xs text-gray-500">UTC</span>
                  </div>
                  {isSubmitted && field.state.meta.errors.length > 0 && (
                    <p className="mt-1 text-xs text-red-600">{field.state.meta.errors[0]}</p>
                  )}
                  {hasDateWarning && field.state.meta.errors.length === 0 && (
                    <p className="mt-1 text-xs text-yellow-600">
                      Trade date is {isBackdated ? 'backdated' : 'in the future'} (not today)
                    </p>
                  )}
                  {tradeDateHoliday?.is_holiday && (
                    <p className="mt-1 text-xs text-yellow-600 bg-yellow-50 px-2 py-1 rounded">
                      Trade date is a public holiday: {tradeDateHoliday.holidays.map(h => `${h.name} (${h.country})`).join(', ')}
                    </p>
                  )}
                  {tradeDateWeekend && (
                    <p className="mt-1 text-xs text-yellow-600">
                      Trade Date falls on a weekend ({tradeDateWeekend})
                    </p>
                  )}
                </>
              );
            }}
          </form.Field>
        </div>
        <div>
          <form.Field
            name="settlement_date"
            validators={{
              onChange: ({ value }) => {
                if (!value) return 'Settlement date is required';
                if (form.state.values.trade_date && value < form.state.values.trade_date) {
                  return 'Settlement date cannot be before trade date';
                }
                return undefined;
              },
            }}
          >
            {(field) => {
              const settlementWeekend = getWeekendDayName(field.state.value);
              return (
              <>
                <label className={labelClassName}>
                  Settlement Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  disabled={isFormDisabled || isAmendLocked(amendMode, 'settlement_date')}
                  className={clsx(inputClassName, isSubmitted && field.state.meta.errors.length > 0 && 'border-red-500', isFormDisabled && 'disabled:bg-gray-100 disabled:cursor-not-allowed')}
                />
                {isSubmitted && field.state.meta.errors.length > 0 && (
                  <p className="mt-1 text-xs text-red-600">{field.state.meta.errors[0]}</p>
                )}
                {settlementDateHoliday?.is_holiday && (
                  <p className="mt-1 text-xs text-yellow-600 bg-yellow-50 px-2 py-1 rounded">
                    Settlement date is a public holiday: {settlementDateHoliday.holidays.map(h => `${h.name} (${h.country})`).join(', ')}
                  </p>
                )}
                {settlementWeekend && (
                  <p className="mt-1 text-xs text-yellow-600">
                    Settlement Date falls on a weekend ({settlementWeekend})
                  </p>
                )}
              </>
              );
            }}
          </form.Field>
        </div>
        {tradeDuration === 'TERM' && (
          <div>
            <form.Field
              name="maturity_date"
              validators={{
                onChange: ({ value }) => {
                  const duration = form.state.values.trade_duration;
                  if (duration === 'TERM') {
                    if (!value) return `Maturity date is required for ${duration} trades`;
                  }
                  if (value && form.state.values.trade_date && value <= form.state.values.trade_date) {
                    return 'Maturity date must be after trade date';
                  }
                  return undefined;
                },
              }}
            >
              {(field) => {
                const duration = form.state.values.trade_duration;
                const requiresMaturity = duration === 'TERM';
                const maturityWeekend = getWeekendDayName(field.state.value);
                  return (
                    <>
                      <label className={labelClassName}>
                        Maturity Date {requiresMaturity && <span className="text-red-500">*</span>}
                      </label>
                      <input
                        type="date"
                        value={field.state.value || ''}
                        onChange={(e) => {
                          const newDate = e.target.value || '';
                          field.handleChange(newDate);
                          // p.12: Notice Period is an independent manual input —
                          // do NOT derive it from Maturity (and vice-versa). The
                          // two were cross-bound for EVERGREEN; both binds removed
                          // so editing one no longer silently moves the other.
                        }}
                        disabled={isFormDisabled || isAmendLocked(amendMode, 'maturity_date')}
                        placeholder="Select maturity date"
                        className={clsx(
                          inputClassName,
                          isSubmitted && field.state.meta.errors.length > 0 && 'border-red-500',
                          isFormDisabled && 'bg-gray-100 disabled:cursor-not-allowed'
                        )}
                      />
                      {isSubmitted && field.state.meta.errors.length > 0 && (
                        <p className="mt-1 text-xs text-red-600">{field.state.meta.errors[0]}</p>
                      )}
                      {maturityWeekend && (
                        <p className="mt-1 text-xs text-yellow-600">
                          Maturity Date falls on a weekend ({maturityWeekend})
                        </p>
                      )}
                    </>
                  );
                }}
              </form.Field>
          </div>
        )}
      </div>
      {tradeDuration === 'EVERGREEN' && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <form.Field
              name="notice_period_days"
              validators={{
                onChange: ({ value }) => {
                  const duration = form.state.values.trade_duration;
                  if (duration !== 'EVERGREEN') return undefined;
                  if (value === undefined || value === null) return 'Notice period is required';
                  // LR-678: the backend requires >= 1 day for Evergreen (Give
                  // Notice derives the maturity date from it, and a 0-day notice
                  // would fix it on the notice date itself). The old bound was
                  // `< 0`, which let 0 through the form into a 400.
                  if (value < 1) return 'Notice period must be at least 1 day';
                  return undefined;
                },
              }}
            >
              {(field) => (
                <>
                  <label className={labelClassName}>
                    Notice Period (Days) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={field.state.value ?? ''}
                    onChange={(e) => {
                      let valStr = e.target.value;
                      if (valStr === '' || /^\d*$/.test(valStr)) {
                        if (valStr.length > 1 && valStr.startsWith('0')) {
                          valStr = valStr.replace(/^0+/, '');
                          if (valStr === '') valStr = '0';
                        }
                        const days = parseInt(valStr, 10);
                        field.handleChange(isNaN(days) ? undefined : days);
                        // p.12: manual input only — no longer derives maturity_date.
                      }
                    }}
                    onFocus={(e) => e.target.select()}
                    disabled={isFormDisabled || isAmendLocked(amendMode, 'notice_period_days')}
                    placeholder="e.g., 3, 7"
                    className={clsx(
                      inputClassName,
                      isSubmitted && field.state.meta.errors.length > 0 && 'border-red-500',
                      isFormDisabled && 'disabled:bg-gray-100 disabled:cursor-not-allowed'
                    )}
                  />
                  {isSubmitted && field.state.meta.errors.length > 0 && (
                    <p className="mt-1 text-xs text-red-600">{field.state.meta.errors[0]}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">Min. termination notice</p>
                </>
              )}
            </form.Field>
          </div>
        </div>
      )}
      {tradeDuration === 'TERM' && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className={labelClassName}>
              Interest Accrual Days
            </label>
            <input
              type="text"
              value={calculatedValues.daysToMaturity ?? ''}
              onChange={(e) => {
                let valStr = e.target.value;
                if (valStr === '' || /^\d*$/.test(valStr)) {
                  if (valStr.length > 1 && valStr.startsWith('0')) {
                    valStr = valStr.replace(/^0+/, '');
                    if (valStr === '') valStr = '0';
                  }
                  const days = parseInt(valStr, 10);
                  // LR-462: accrual runs from settlement date, so N accrual days
                  // means maturity = settlementDate + N (was tradeDate-based).
                  if (days > 0 && settlementDate) {
                    const settlementDateObj = new Date(settlementDate);
                    const maturityDateObj = new Date(settlementDateObj);
                    maturityDateObj.setDate(maturityDateObj.getDate() + days);
                    form.setFieldValue('maturity_date', maturityDateObj.toISOString().split('T')[0]);
                  }
                }
              }}
              onFocus={(e) => e.target.select()}
              disabled={isFormDisabled || !settlementDate || isAmendLocked(amendMode, 'maturity_date')}
              placeholder="Enter days"
              title="Edit to auto-calculate maturity date from settlement date"
              className={clsx(inputClassName, (isFormDisabled || !settlementDate) && 'disabled:bg-gray-100 disabled:cursor-not-allowed')}
            />
          </div>
          <div>
            <label className={labelClassName}>
              Total Expected Return ({displayCurrency || 'USD'})
            </label>
            <input
              type="text"
              value={calculatedValues.totalReturn?.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }) || '-'}
              readOnly
              className={clsx(readOnlyInputClassName, 'text-gray-900 font-semibold')}
            />
          </div>
          {/* The calculator still computes closingSettlement for GMSLA — only
              the display is gated (there it equals Contract Value exactly). */}
          {showClosingSettlement && (
            <div>
              <label className={labelClassName}>
                Closing Settlement ({displayCurrency || 'USD'})
              </label>
              <input
                type="text"
                value={calculatedValues.closingSettlement?.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }) || '-'}
                readOnly
                title={`Opening: ${calculatedValues.netSettlementSecurity?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || 0} + Cash-flow Interest: ${gmraCashFlowInterest?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || 0}. Cash-flow direction (not signed PnL — see Total Expected Return for our PnL).`}
                className={clsx(readOnlyInputClassName, 'text-purple-600 font-semibold')}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
